import { Layout, Spin } from 'antd';
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../routes/routes';

import { Navigation } from './Navigation';

const { Sider, Content } = Layout;

const LayoutS = styled(Layout)`
  height: 100vh;

  .ant-layout-sider {
    display: flex;
    flex-direction: column;
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .ant-layout-sider-zero-width-trigger {
    z-index: 200;
    top: 12px;
    width: 32px;
    height: 32px;
    line-height: 32px;
    font-size: 14px;
    border-radius: 0 6px 6px 0;
    right: -32px;

    @media (min-width: 769px) {
      display: none;
    }
  }
`;

const ContentS = styled(Content)`
  background: #f4f7f6;
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: auto;

  @media (max-width: 768px) {
    padding: 12px;
    padding-top: 56px;
  }
`;

const LogoContainer = styled.div<{ $collapsed: boolean }>`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0 16px;

  h1 {
    color: #ff8e8e;
    font-size: 20px;
    font-weight: 800;
    margin: 0;
    white-space: nowrap;
    letter-spacing: 1px;
  }

  .logo-full-text {
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  }

  .logo-collapsed-text {
    display: ${({ $collapsed }) => ($collapsed ? 'block' : 'none')};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const DevBadge = styled.div`
  display: flex;
  justify-content: center;
  background: #f59e0b;
  color: #000;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 0;
  letter-spacing: 1px;
  pointer-events: none;
`;

export const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, logout, user } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  return (
    <LayoutS>
      <Sider
        collapsible
        collapsed={collapsed}
        breakpoint="md"
        collapsedWidth={isMobile ? 0 : 80}
        onBreakpoint={(broken) => setIsMobile(broken)}
        onCollapse={(value) => setCollapsed(value)}
      >
        <LogoContainer $collapsed={collapsed}>
          <h1>
            <span className="logo-full-text">AVON MARKET</span>
            <span className="logo-collapsed-text">SA</span>
          </h1>
        </LogoContainer>
        {process.env.NODE_ENV !== 'production' && <DevBadge>DEV</DevBadge>}
        <Navigation
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          user={user}
          onLogout={handleLogout}
        />
      </Sider>
      <Layout>
        <ContentS>{children}</ContentS>
      </Layout>
    </LayoutS>
  );
};
